import React, { useState } from 'react';
import { SectionHeader } from '../components/ui/SectionHeader';
import { SERVICES } from '../constants';
import { Users, Music2, Film, Globe, Sparkles, Sliders, Layers, MessageSquare, GraduationCap, ChevronLeft, Check, CheckCircle, ArrowRight, ShoppingBag, Send, Link as LinkIcon } from 'lucide-react';
import { Service } from '../types';
import { SEO } from '../components/SEO';
import { sendForm } from '../services/formService';

const iconMap: Record<string, React.ReactNode> = {
  'Users': <Users className="w-8 h-8" />,
  'Music2': <Music2 className="w-8 h-8" />,
  'Film': <Film className="w-8 h-8" />,
  'Globe': <Globe className="w-8 h-8" />,
  'Sliders': <Sliders className="w-8 h-8" />,
  'Layers': <Layers className="w-8 h-8" />,
  'MessageSquare': <MessageSquare className="w-8 h-8" />,
  'GraduationCap': <GraduationCap className="w-8 h-8" />,
};

type ViewMode = 'grid' | 'detail' | 'form';

export const Services: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  
  // Form State
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    selectedServices: [] as string[],
    message: '',
    link: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success'>('idle');

  const handleServiceSelect = (serviceId: string) => {
    setFormData(prev => {
      const exists = prev.selectedServices.includes(serviceId);
      if (exists) {
        return { ...prev, selectedServices: prev.selectedServices.filter(id => id !== serviceId) };
      } else {
        return { ...prev, selectedServices: [...prev.selectedServices, serviceId] };
      }
    });
  };

  const openForm = (service?: Service) => {
    if (service) {
        setFormData(prev => ({
            ...prev,
            selectedServices: prev.selectedServices.includes(service.id) ? prev.selectedServices : [...prev.selectedServices, service.id]
        }));
    }
    setViewMode('form');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const serviceNames = formData.selectedServices.map(id => SERVICES.find(s => s.id === id)?.title).join(', ');
    const submissionData = { ...formData, selectedServices: serviceNames };

    const { success, error } = await sendForm('service-request', submissionData);

    if (success) {
      setFormStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert("Error submitting form: " + error);
      setFormStatus('idle');
    }
  };

  // ----------------------------------------------------------------------
  // VIEW: SUCCESS
  // ----------------------------------------------------------------------
  if (formStatus === 'success') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center container mx-auto px-6 py-12">
        <SEO title="Request Received" description="Your service request has been received." />
        <div className="bg-white/10 border border-astral-purple/30 p-8 md:p-12 text-center max-w-2xl w-full relative overflow-hidden animate-in fade-in zoom-in duration-500">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-astral-purple opacity-20 blur-[50px]"></div>
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-astral-purple/20 flex items-center justify-center border border-astral-purple/50">
              <CheckCircle className="w-10 h-10 text-astral-purple" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold uppercase text-white mb-4">Request Received</h2>
          <p className="text-slate-200 text-base md:text-lg mb-8 leading-relaxed">
            Thank you for your inquiry. Your request for services has been successfully submitted.<br/>
            Our team will review the details and contact you within 24-48 hours.
          </p>
          <button
            onClick={() => {
              setFormStatus('idle');
              setViewMode('grid');
              setFormData({ email: '', phone: '', selectedServices: [], message: '', link: '' });
            }}
            className="px-8 py-3 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-astral-purple hover:text-white transition-colors"
          >
            Return to Agency
          </button>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------------------------
  // VIEW: FORM
  // ----------------------------------------------------------------------
  if (viewMode === 'form') {
    const showLinkField = formData.selectedServices.some(id => id === 'srv_mix' || id === 'srv_master');

    return (
        <div className="py-12 animate-in fade-in slide-in-from-bottom-5 duration-500 min-h-screen">
            <SEO title="Book a Service" description="Book professional audio services: mixing, mastering, and production." url="/services/book" />
            <div className="container mx-auto px-6 max-w-4xl">
                <button 
                    onClick={() => setViewMode(selectedService ? 'detail' : 'grid')}
                    className="group flex items-center gap-2 mb-8 text-gray-300 hover:text-white transition-colors"
                >
                    <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold uppercase tracking-widest">Back</span>
                </button>

                <div className="bg-black/60 backdrop-blur-md border border-white/10 p-8 md:p-12 rounded-xl relative shadow-2xl">
                     <div className="mb-10">
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-2 uppercase">Secure Your Slot</h2>
                        <p className="text-slate-300 font-light">Complete the protocol below to book our studio services.</p>
                     </div>

                     <form name="service-request" onSubmit={handleFormSubmit} className="space-y-8">
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Email Address</label>
                                <input 
                                    required
                                    type="email" 
                                    name="email"
                                    value={formData.email}
                                    onChange={e => setFormData({...formData, email: e.target.value})}
                                    className="w-full bg-black/60 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                    placeholder="you@domain.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Phone Number</label>
                                <input 
                                    required
                                    type="tel" 
                                    name="phone"
                                    value={formData.phone}
                                    onChange={e => setFormData({...formData, phone: e.target.value})}
                                    className="w-full bg-black/60 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                    placeholder="+1 234 567 890"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Select Services</label>
                            <div className="grid md:grid-cols-2 gap-4">
                                {SERVICES.map(service => (
                                    <div 
                                        key={service.id}
                                        onClick={() => handleServiceSelect(service.id)}
                                        className={`
                                            cursor-pointer border p-4 flex items-center gap-4 transition-all duration-300
                                            ${formData.selectedServices.includes(service.id) 
                                                ? 'bg-astral-purple/20 border-astral-purple' 
                                                : 'bg-black/40 border-white/10 hover:border-white/30'}
                                        `}
                                    >
                                        <div className={`
                                            w-5 h-5 rounded-full border flex items-center justify-center
                                            ${formData.selectedServices.includes(service.id) ? 'border-astral-purple bg-astral-purple' : 'border-gray-500'}
                                        `}>
                                            {formData.selectedServices.includes(service.id) && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        <div>
                                            <h4 className="text-white font-bold text-sm uppercase">{service.title}</h4>
                                            <p className="text-xs text-gray-400">{service.price}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {showLinkField && (
                            <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan flex items-center gap-2">
                                    <LinkIcon className="w-3 h-3" /> Link to Stems / Demo
                                </label>
                                <input 
                                    required
                                    type="url" 
                                    name="link"
                                    value={formData.link}
                                    onChange={e => setFormData({...formData, link: e.target.value})}
                                    className="w-full bg-black/60 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                    placeholder="Dropbox / Drive / WeTransfer Link"
                                />
                                <p className="text-[10px] text-slate-400">Required for mixing and mastering services.</p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Message / Details</label>
                            <textarea 
                                required
                                rows={5}
                                name="message"
                                value={formData.message}
                                onChange={e => setFormData({...formData, message: e.target.value})}
                                className="w-full bg-black/60 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                placeholder="Tell us about your project..."
                            />
                        </div>

                        <button 
                            type="submit"
                            className="w-full bg-white text-black font-display font-bold uppercase tracking-[0.2em] py-5 hover:bg-astral-purple hover:text-white transition-all flex items-center justify-center gap-2 group"
                        >
                            Send Request <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                     </form>
                </div>
            </div>
        </div>
    );
  }

  // ----------------------------------------------------------------------
  // VIEW: DETAIL
  // ----------------------------------------------------------------------
  if (viewMode === 'detail' && selectedService) {
    return (
      <div className="py-12 animate-in fade-in slide-in-from-bottom-5 duration-500 min-h-screen">
        <SEO 
            title={selectedService.title} 
            description={selectedService.description} 
            url={`/services/${selectedService.id}`}
        />
        <div className="container mx-auto px-6">
          <button 
            onClick={() => { setSelectedService(null); setViewMode('grid'); }}
            className="group flex items-center gap-2 mb-8 text-gray-300 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs font-bold uppercase tracking-widest">Back to Agency</span>
          </button>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Info */}
            <div className="bg-black/60 backdrop-blur-md p-8 rounded-xl border border-white/5">
              <div className="w-20 h-20 bg-astral-purple/20 border border-astral-purple/50 rounded-2xl flex items-center justify-center text-astral-purple mb-8 shadow-[0_0_20px_rgba(96,37,192,0.3)]">
                 {iconMap[selectedService.icon]}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold uppercase text-white mb-6 text-glow">
                {selectedService.title}
              </h1>
              <p className="text-3xl text-astral-cyan font-light mb-8 font-display text-shadow">
                {selectedService.price}
              </p>
              
              <div className="prose prose-invert max-w-none mb-10">
                {/* Enhanced Readability Container */}
                <div className="bg-black/60 p-6 rounded-lg border-l-4 border-astral-purple">
                    <p className="text-slate-100 text-lg leading-relaxed shadow-black drop-shadow-md">
                    {selectedService.detailedDescription}
                    </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => openForm(selectedService)}
                  className="flex-1 px-8 py-4 bg-white text-black font-display font-bold uppercase tracking-widest text-center hover:bg-astral-purple hover:text-white transition-all flex items-center justify-center gap-3 shadow-[0_0_15px_rgba(255,255,255,0.3)] hover:shadow-[0_0_20px_rgba(96,37,192,0.6)]"
                >
                   <ShoppingBag className="w-4 h-4" /> Book Service
                </button>
                <button 
                   onClick={() => { setSelectedService(null); setViewMode('grid'); }}
                   className="px-8 py-4 border border-white/30 text-white font-display font-bold uppercase tracking-widest hover:bg-white/10 transition-colors backdrop-blur-sm"
                >
                   Back
                </button>
              </div>
            </div>

            {/* Right Features */}
            <div className="bg-black/60 backdrop-blur-md border border-white/10 p-8 md:p-12 rounded-xl relative shadow-2xl">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-astral-cyan opacity-20 blur-[50px] rounded-full pointer-events-none"></div>
              
              <h3 className="text-xl font-display font-bold uppercase text-white mb-8 flex items-center gap-3 relative z-10 text-glow">
                 <Sparkles className="w-5 h-5 text-astral-cyan" /> Includes
              </h3>
              
              <ul className="space-y-6 relative z-10">
                {selectedService.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-4 group">
                    <div className="w-6 h-6 rounded-full bg-astral-purple/20 flex items-center justify-center border border-astral-purple/50 group-hover:bg-astral-purple/40 transition-colors mt-0.5 shrink-0">
                      <Check className="w-3 h-3 text-astral-purple" />
                    </div>
                    <span className="text-white text-lg font-medium drop-shadow-md">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-12 pt-8 border-t border-white/10 relative z-10">
                <p className="text-xs text-astral-cyan uppercase tracking-widest mb-2 font-bold">Turnaround Time</p>
                <p className="text-white drop-shadow-md">Typically 3-5 business days depending on project scale.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------------------------
  // VIEW: GRID
  // ----------------------------------------------------------------------
  const servicesStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": SERVICES.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
            "@type": "Service",
            "name": service.title,
            "description": service.description,
            "offers": {
                "@type": "Offer",
                "price": service.price,
                "priceCurrency": "EUR"
            }
        }
    }))
  };

  return (
    <div className="py-12">
      <SEO 
        title="Creative Agency" 
        description="Professional electronic music services: Mixing, Mastering, Ghost Production, and Marketing." 
        url="/services"
        keywords={["audio mixing", "mastering services", "ghost production", "music marketing", "electronic music services"]}
        structuredData={servicesStructuredData}
      />
      <div className="container mx-auto px-6">
        <SectionHeader subtitle="The Agency" title="Professional Services" />
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((service, idx) => (
            <div 
              key={service.id} 
              onClick={() => { setSelectedService(service); setViewMode('detail'); }}
              className="bg-black/60 backdrop-blur-md border border-white/10 p-8 hover:bg-black/60 hover:border-astral-purple/50 transition-all duration-300 cursor-pointer group flex flex-col h-full relative overflow-hidden rounded-sm shadow-lg hover:shadow-astral-purple/20"
            >
              {/* Hover Glow */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-astral-purple/20 blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>

              <div className="flex justify-between items-start mb-6 relative z-10">
                <span className="font-mono text-astral-cyan text-xs font-bold uppercase tracking-widest">
                  0{idx + 1}
                </span>
                <div className="text-gray-300 group-hover:text-white transition-colors">
                   {iconMap[service.icon]}
                </div>
              </div>
              
              <h3 className="text-2xl font-display font-bold uppercase mb-3 text-white group-hover:text-astral-purple transition-colors relative z-10 text-glow">
                {service.title}
              </h3>
              
              <p className="text-slate-200 mb-6 leading-relaxed relative z-10 text-sm flex-grow font-normal drop-shadow-md">
                {service.description}
              </p>

              <div className="mt-auto relative z-10 pt-6 border-t border-white/10 flex items-center justify-between group-hover:border-white/30 transition-colors">
                 <span className="text-astral-cyan font-mono text-sm font-bold">{service.price}</span>
                 <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all">
                    <ArrowRight className="w-4 h-4" />
                 </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};